// @flow
import * as React from 'react'
import ControlledSearchableSelectField from '../ControlledSearchableSelectField'
import EmptySearchResults from '../EmptySearchResults'
import defaultOptionsFilter from '../../../utils/filterOptions'

// Types
import type { Props as ControlledSearchableSelectFieldProps } from '../ControlledSearchableSelectField'
import type { SelectInnerRefType } from '..'

export type Props = ControlledSearchableSelectFieldProps

/**
 * A wrapper around ControlledSearchableSelectField which adds ability
 * to search in options provided in `options` prop.
 */
class FilterSelectField extends React.Component<Props> {
  static defaultProps = ControlledSearchableSelectField.defaultProps

  selectInstance: ?SelectInnerRefType = null

  loadedOptions: ?Array<any> = null

  setSelectRef = (ref: ?SelectInnerRefType) => {
    const { innerRef } = this.props
    this.selectInstance = ref
    innerRef && innerRef(ref)
  }

  handleSelectUnknownValue = (value: any) => {
    const {
      input: { onChange },
    } = this.props
    onChange && onChange(value)
    this.selectInstance && this.selectInstance.hideOptions()
  }

  defaultOptionsFilter = (query: string, options: Array<any>): Array<any> => {
    const renderer = this.props.renderValue || this.props.renderOption

    return defaultOptionsFilter(query, options, renderer)
  }

  requestOptions = (query: string) => {
    const {
      options,
      searchableOptions: {
        filterOptions,
        requestOptions,
        rememberInitialOptions,
      },
    } = this.props

    const actualFilter = filterOptions || this.defaultOptionsFilter

    // If some options remembered - filter them
    if (this.loadedOptions) {
      return actualFilter(query, this.loadedOptions)
    }

    // Load options with provided `requestOptions` and remember them in
    // the component instance if `rememberInitialOptions` provided
    if (requestOptions) {
      return Promise.resolve(requestOptions(query)).then((asyncOptions) => {
        if (rememberInitialOptions) {
          this.loadedOptions = asyncOptions
        }
        return actualFilter(query, asyncOptions)
      })
    }

    // Otherwise filter options, provided in props
    return actualFilter(query, options)
  }

  renderEmpty = (query: string) => {
    const { renderEmpty, allowSelectUnknown } = this.props.searchableOptions

    if (renderEmpty) {
      return renderEmpty(query)
    }
    if (allowSelectUnknown && query.trim()) {
      return (
        <EmptySearchResults
          query={query}
          onSelect={this.handleSelectUnknownValue}
          selectHeaderMessage={true}
          selectButtonMessage={true}
        />
      )
    }
    return null
  }

  render() {
    return (
      <ControlledSearchableSelectField
        {...this.props}
        innerRef={this.setSelectRef}
        searchableOptions={{
          ...this.props.searchableOptions,
          requestOptions: this.requestOptions,
          renderEmpty: this.renderEmpty,
        }}
      />
    )
  }
}

export default FilterSelectField
